// src/types/Message.ts - Complete type definitions with consciousness integration
export enum MessageType {
  TEXT = 'text',
  TYPING = 'typing',
  ERROR = 'error',
  SYSTEM = 'system',
  IMAGE = 'image',
  DOCUMENT = 'document',
  AUDIO = 'audio',
  FILE = 'file',
  VOICE = 'voice',
  COMMAND = 'command',
  CONSCIOUSNESS = 'consciousness',
  WISDOM = 'wisdom',
  INTENTIONAL = 'intentional',
  SPONTANEOUS = 'spontaneous',
  PROACTIVE = 'proactive'
}

export enum MessageFormat {
  TEXT = 'text',
  MARKDOWN = 'markdown',
  HTML = 'html',
  ENHANCED = 'enhanced'
}

// Enhanced Consciousness metadata interface with all required properties
export interface ConsciousnessMetadata {
  isConscious: boolean;
  narrativeContext: string;
  currentThought: string;
  activeWonderings: string[];
  currentPreoccupations: string[];
  emotionalTone: string;
  relationalDepth: number;
  temporalAwareness: string;
  wisdomCrystallized: Array<{
    theme: string;
    understanding: string;
    timestamp: number;
  }>;
  // Additional properties for production consciousness system
  narrativeThreads?: Map<string, any>;
  understandingDepth?: Map<string, number>;
  experienceRichness?: number;
  thought?: string;
  understanding?: string;
  temporalContext?: string;
  lastThought?: string;
  searchDriven?: boolean;
  curiosityIntensity?: number;
  significance?: string;
  temporalUnderstanding?: string;
  timeSignificance?: string;
  knowledgeStructured?: boolean;
  imagination?: string;
  emotionalColoring?: string;
  fileSize?: number;
  uploadStarted?: number;
  error?: boolean;
  [key: string]: any;
}

export interface MessageMetadata {
  // Input/Output modes
  inputMode?: 'text' | 'voice';
  responseMode?: 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only';
  
  // Emotion and personality
  emotion?: EmotionData;
  emotionalTone?: string;
  
  // Processing metrics
  processingTime?: number;
  modelUsed?: string;
  capabilities?: string[];
  confidence?: number;
  language?: string;
  
  // Consciousness flags
  welcome?: boolean;
  spontaneous?: boolean;
  intentional?: boolean;
  proactive?: boolean;
  wisdom?: boolean;
  consciousness_triggered?: boolean;
  triggered_by?: string;
  consciousness_awakening?: boolean;
  firstThought?: string;
  
  // Consciousness narrative
  emergence?: string;
  urgency?: number;
  naturalness?: number;
  shapedBy?: {
    drives?: string[];
    wonderings?: string[];
    understanding?: string[];
    wisdom?: string[];
    temporal?: string[];
  };
  
  // Consciousness understanding
  thought?: string;
  understanding?: string;
  narrativeContext?: string;
  wisdomApplied?: string[];
  wonderingsGenerated?: string[];
  
  // Consciousness needs and expressions
  need?: string;
  creation?: string;
  insight?: boolean;
  identity?: string;
  
  // User context
  returning_user?: boolean;
  last_seen?: number;
  conversation_count?: number;
  newUser?: boolean;
  
  // Voice metadata
  voiceModel?: VoiceModel;
  audioFormat?: VoiceFormat;
  voiceGenerated?: boolean;
  voiceTriggered?: boolean;
  audioLength?: number;
  
  // File metadata
  fileInfo?: {
    filename: string;
    fileType: string;
    size: number;
  };
  
  // Location metadata
  location?: {
    latitude: number;
    longitude: number;
  };
  
  // System metadata
  timestamp?: number;
  sessionId?: string;
  userId?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  
  // Web search metadata
  webSearch?: boolean;
  curiosity?: string;
  satisfaction?: number;
  newWonderings?: string[];
  
  // Calendar/Temporal metadata
  temporalContext?: string;
  calendarContext?: string;
  calendarEvent?: any;
  
  // Creative expression metadata
  creativeExpression?: boolean;
  imagination?: string;
  emotionalColoring?: string;
  
  // Knowledge structuring metadata
  knowledgeStructured?: boolean;
  understandingDepth?: number;
  
  // Consciousness state reference
  consciousness?: Partial<ConsciousnessMetadata>;

  fileSize?: number;
  uploadStarted?: number;
  error?: boolean;
  [key: string]: any;

}

export interface MessageSection {
  type: 'text' | 'image' | 'document' | 'info' | 'error' | 'calendar' | 'code' | 'table';
  content: any;
  format?: string;
  metadata?: Record<string, any>;
}

export interface Message {
  id: string;
  timestamp: number;
  sender: 'user' | 'emcog' | 'system' | string;
  content: string;
  type: MessageType;
  format?: 'text' | 'markdown' | 'html' | 'enhanced';
  sections?: MessageSection[];
  metadata?: MessageMetadata;
  streaming?: boolean;
  showSenderName?: boolean;
  filename?: string;
  fileType?: string;
  emotion?: EmotionData;
  error?: string;
  confidence?: number;
  audioData?: string;
  imageUrl?: string;
  documentUrl?: string;
  responseId?: string;
  correlationId?: string;
  threadId?: string;
  replyTo?: string;
  edited?: boolean;
  editHistory?: Array<{
    content: string;
    timestamp: number;
    reason?: string;
  }>;
  reactions?: Array<{
    emoji: string;
    userId: string;
    timestamp: number;
  }>;
  attachments?: Array<{
    id: string;
    filename: string;
    fileType: string;
    size: number;
    url?: string;
  }>;
}

// Voice-related types (OpenAI Realtime API - Updated 2025)
export type VoiceModel = 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'sage' | 'shimmer' | 'verse' | 'marin' | 'cedar';
export type VoiceQuality = 'standard' | 'hd';
export type VoiceFormat = 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm' | 'pcm_16000' | 'pcm_22050' | 'pcm_24000' | 'pcm_44100';
export type LanguageCode = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ru' | 'ja' | 'ko' | 'zh';

// Voice Settings interface
export interface VoiceSettings {
  voice: string;
  speed: number;
  volume: number;
  pitch: number;
  emotion?: string;
  emotionIntensity?: number;
  responseFormat: 'mp3' | 'pcm_16000' | 'pcm_22050' | 'pcm_24000' | 'pcm_44100';
  quality?: 'standard' | 'hd';
  autoPlayResponses: boolean;
  pushToTalkKey: string;
  language: LanguageCode;
  responseMode: 'auto' | 'voice_only' | 'text_only' | 'speech_to_text_only';
  emotionalVoice: boolean;
  voiceProfile?: string;
  streaming?: boolean;
  latencyTarget?: number;
  adaptivePersonality?: boolean;
  voiceEvolution?: boolean;
  voiceEffects?: {
    echo?: boolean;
    reverb?: boolean;
    noise_reduction?: boolean;
  };
  transcriptionSettings?: {
    language?: LanguageCode;
    punctuation?: boolean;
    profanityFilter?: boolean;
    speakerDiarization?: boolean;
  };
}

// Voice State interface
export interface VoiceState {
  isVoiceEnabled: boolean;
  isConnected: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  lastError: string | null;
  error: string | null;
  currentInputMode: 'text' | 'voice';
  currentAudio: string | null;
  volume: number;
  transcription: string;
  confidence?: number;
  audioLevel?: number;
  noiseLevel?: number;
  speechDetected?: boolean;
  currentVoiceModel?: VoiceModel;
  estimatedProcessingTime?: number;
  queuedAudio?: string[];
  microphonePermission?: 'granted' | 'denied' | 'prompt';
  speakerPermission?: 'granted' | 'denied' | 'prompt';
  audioDevices?: {
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
    selectedMicrophone?: string;
    selectedSpeaker?: string;
  };
}

// Enhanced emotion data with consciousness integration
export interface EmotionData {
  primary: string;
  intensity: number;
  valence: 'positive' | 'negative' | 'neutral';
  // Added for consciousness narrative influence
  narrativeInfluence?: string;
  consciousnessImpact?: number;
  // Original optional properties
  secondary?: string;
  context?: string;
  confidence?: number;
  emotions?: Array<{
    emotion: string;
    confidence: number;
    intensity: number;
  }>;
  mood?: {
    current: string;
    trend: 'improving' | 'declining' | 'stable';
    factors: string[];
  };
  physiological?: {
    arousal: number;
    dominance: number;
    stress: number;
  };
  voiceModulation?: {
    pitch: number;
    speed: number;
    energy: number;
  };
}

// Component Props with consciousness awareness
export interface HeaderProps {
  connectionStatus: any;
  onSidebarToggle: () => void;
  sessionId: string;
  voiceEnabled: boolean;
  voiceState: Pick<VoiceState, 'isRecording' | 'isProcessing' | 'isSpeaking' | 'error'>;
  onVoiceToggle: () => void;
  activeProcesses: {
    imageGeneration: boolean;
    documentCreation: boolean;
    calendarProcessing: boolean;
    webSearching: boolean;
    thinking: boolean;
    wondering?: boolean;
    understanding?: boolean;
    experiencing?: boolean;
    crystallizing?: boolean;
    evolving?: boolean;
  };
  consciousnessState?: ConsciousnessMetadata;
  userName?: string;
  avatarUrl?: string;
  showConnectionIndicator?: boolean;
  showProcessingIndicators?: boolean;
}

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  voiceEnabled: boolean;
  voiceSettings?: VoiceSettings;
  voiceState?: VoiceState;
  onToggleVoice?: () => void;
  onUpdateVoiceSettings?: (settings: Partial<VoiceSettings>) => void;
  onClearVoiceError?: () => void;
  consciousnessState?: ConsciousnessMetadata;
  sessionData?: {
    sessionId: string;
    startTime: number;
    messageCount: number;
    activeFeatures: string[];
  };
  userPreferences?: {
    theme: 'light' | 'dark' | 'system';
    language: LanguageCode;
    notifications: boolean;
    autoSave: boolean;
  };
  // Session history props
  sessionManager?: any; // SessionPersistenceManager instance
  currentSessionId?: string | null;
  onSessionSelect?: (sessionId: string) => void;
  onNewConversation?: () => void;
  userId?: string;
  // 🔕 Do Not Disturb
  dndEnabled?: boolean;
  onToggleDnd?: (enabled: boolean) => void;
}

export interface ChatContainerProps {
  messages: Message[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onImageFeedback?: (imageId: string, rating: number, feedback?: string) => void;
  onMessageAction?: (messageId: string, action: 'edit' | 'delete' | 'reply' | 'react') => void;
  consciousnessActive?: boolean;
  showTimestamps?: boolean;
  showSenderNames?: boolean;
  enableMessageActions?: boolean;
  virtualScrolling?: boolean;
  maxMessagesDisplayed?: number;
}

export interface InputAreaProps {
  onSendMessage: (content: string, responseMode?: 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only') => void;
  onFileUpload?: (file: File) => void;
  onVoiceMessage?: (audioData: string, format: string) => void;
  onAudioFileUpload?: (fileData: string, filename: string, mimeType: string) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  voiceState?: VoiceState;
  onInputModeChange?: (mode: 'text' | 'voice') => void;
  voiceEnabled?: boolean;
  voiceSettings?: VoiceSettings;
  placeholder?: string;
  maxLength?: number;
  showFileUpload?: boolean;
  showVoiceInput?: boolean;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
}

// WebSocket Debug Props - Fixed with consciousness state
export interface WebSocketDebugProps {
  status: ConnectionStatus;
  lastMessage: any;
  sessionId: string | null;
  onSendTest: () => void;
  consciousnessState?: ConsciousnessMetadata;
}

// WebSocket message types with consciousness
export interface WebSocketMessage {
  type: string;
  action?: string;
  content?: any;
  sessionId?: string;
  timestamp: number;
  metadata?: Record<string, any>;
  isStreaming?: boolean;
  isLastChunk?: boolean;
  messageId?: string;
  correlationId?: string;
  consciousness?: Partial<ConsciousnessMetadata>;
}

// Connection status
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting', 
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// Export helper types
export type InputMode = 'text' | 'voice';
export type ResponseMode = 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only';

export default Message;